import React, { useState, useEffect } from "react";
import axios from "axios";
import "./RoleDetails.scss";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../../components/Loader/Loader";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const RoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [roleData, setRoleData] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  const tokens = JSON.parse(localStorage.getItem("ERPTokens"));

  useEffect(() => {
    const fetchRoleData = async () => {
      setIsLoading(true);
      try {
        // First, fetch the role name
        const roleResponse = await axios.get(`${BE_URL}/api/roles/${id}`, {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        });

        if (roleResponse.data && roleResponse.data.name) {
          setRoleName(roleResponse.data.name);
        }

        // Then fetch the permissions
        const response = await axios.get(
          `${BE_URL}/api/roles/${id}/permissions`,
          {
            headers: {
              Authorization: `Bearer ${tokens.accessToken}`,
            },
          }
        );

        const formattedData = formatDataForState(response.data);
        setRoleData(formattedData);

        // Initialize expanded state for all menus
        const menuStates = {};
        formattedData.forEach((menu) => {
          menuStates[menu.id] = true; // Set all menus to be expanded by default
        });
        setExpandedMenus(menuStates);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          try {
            const newToken = await refreshToken();
            const response = await axios.get(
              `${BE_URL}/api/roles/${id}/permissions`,
              {
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              }
            );
            setRoleData(formatDataForState(response.data));
          } catch (refreshError) {
            console.error("Error after refreshing token:", refreshError);
            showNotification("Session expired. Please login again.", "error");
          }
        } else {
          console.error("Error fetching role data:", error);
          showNotification("Error fetching role permissions.", "error");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoleData();
  }, [id]);

  const refreshToken = async () => {
    try {
      const refreshResponse = await axios.post(`${BE_URL}/api/refresh`, {
        refreshToken: localStorage.getItem("refreshtoken"),
      });
      const newToken = refreshResponse.data.accessToken;
      localStorage.setItem(
        "ERPTokens",
        JSON.stringify({ ...tokens, accessToken: newToken })
      );
      return newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      throw error;
    }
  };

  const formatDataForState = (data) => {
    return data.map((menuItem) => ({
      ...menuItem,
      dropdowns: menuItem.dropdowns.map((dd) => ({
        ...dd,
        permissions: {
          can_create: dd.permissions.can_create,
          can_read: dd.permissions.can_read,
          can_update: dd.permissions.can_update,
          can_delete: dd.permissions.can_delete,
        },
      })),
    }));
  };

  const handleCheckboxChange = async (
    menuId,
    ddId,
    permissionType,
    isChecked
  ) => {
    setIsLoading(true);

    // Find the updated permissions in the current state
    const updatedPermissions = roleData
      .find((menuItem) => menuItem.id === menuId)
      .dropdowns.find((dd) => dd.id === ddId).permissions;

    const permissionsUpdate = {
      ddId,
      permissions: {
        ...updatedPermissions,
        [permissionType]: isChecked,
      },
    };

    try {
      const response = await axios.put(
        `${BE_URL}/api/roles/${id}/permissions`,
        permissionsUpdate,
        {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        }
      );

      // Only update state if API call was successful
      if (response.status === 200) {
        // Prepare a new roleData array with the updated permissions
        const newRoleData = roleData.map((menuItem) => {
          if (menuItem.id === menuId) {
            return {
              ...menuItem,
              dropdowns: menuItem.dropdowns.map((dd) => {
                if (dd.id === ddId) {
                  return {
                    ...dd,
                    permissions: {
                      ...dd.permissions,
                      [permissionType]: isChecked,
                    },
                  };
                }
                return dd;
              }),
            };
          }
          return menuItem;
        });

        // Update local state with the new data
        setRoleData(newRoleData);
        showNotification("Permission updated successfully", "success");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Token might be expired, try refreshing it
        try {
          await refreshToken();
          handleCheckboxChange(menuId, ddId, permissionType, isChecked);
        } catch (refreshError) {
          showNotification("Session expired. Please login again.", "error");
        }
      } else {
        // Handle other types of errors
        showNotification("Failed to update permission", "error");
        console.error("Error updating permissions:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMenuExpand = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  return (
    <div className="role-details">
      {isLoading && <Loader />}

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-icon">
              {notification.type === "success" ? "✓" : "!"}
            </span>
            <span className="notification-message">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="role-details-header">
        <div className="role-actions">
          <button
            className="back-button"
            onClick={() => navigate("/admin/roles")}
          >
            <span className="back-icon">←</span> Back to Roles
          </button>
        </div>
        <div className="role-info">
          <h1 className="role-title">
            {roleName}
            <span className="role-id">ID: {id}</span>
          </h1>
          <p className="role-description">
            Manage permissions for this role by enabling or disabling specific
            actions.
          </p>
        </div>
      </div>

      <div className="role-details-content">
        <div className="permissions-legend">
          <div className="permission-type">
            <div className="permission-icon read"></div>
            <div className="permission-label">
              Read - View data and information
            </div>
          </div>
          <div className="permission-type">
            <div className="permission-icon create"></div>
            <div className="permission-label">Create - Add new entries</div>
          </div>
          <div className="permission-type">
            <div className="permission-icon update"></div>
            <div className="permission-label">Update - Edit existing data</div>
          </div>
          <div className="permission-type">
            <div className="permission-icon delete"></div>
            <div className="permission-label">Delete - Remove data</div>
          </div>
        </div>

        <div className="permissions-grid">
          {roleData.map((menuItem) => (
            <div key={menuItem.id} className="menu-card">
              <div
                className="menu-header"
                onClick={() => toggleMenuExpand(menuItem.id)}
              >
                <h3 className="menu-title">{menuItem.name}</h3>
                <div
                  className={`menu-toggle ${
                    expandedMenus[menuItem.id] ? "expanded" : ""
                  }`}
                >
                  <svg
                    width="14"
                    height="8"
                    viewBox="0 0 14 8"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L7 7L13 1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {expandedMenus[menuItem.id] && (
                <div className="menu-dropdowns">
                  <table className="permissions-table">
                    <thead>
                      <tr>
                        <th className="feature-column">Feature</th>
                        <th className="permission-column">Read</th>
                        <th className="permission-column">Create</th>
                        <th className="permission-column">Update</th>
                        <th className="permission-column">Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {menuItem.dropdowns.map((dd) => (
                        <tr key={dd.id} className="dropdown-row">
                          <td className="feature-name">{dd.name}</td>
                          <td className="permission-cell">
                            <label className="checkbox-container">
                              <input
                                type="checkbox"
                                checked={dd.permissions.can_read}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    menuItem.id,
                                    dd.id,
                                    "can_read",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="permission-cell">
                            <label className="checkbox-container">
                              <input
                                type="checkbox"
                                checked={dd.permissions.can_create}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    menuItem.id,
                                    dd.id,
                                    "can_create",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="permission-cell">
                            <label className="checkbox-container">
                              <input
                                type="checkbox"
                                checked={dd.permissions.can_update}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    menuItem.id,
                                    dd.id,
                                    "can_update",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="permission-cell">
                            <label className="checkbox-container">
                              <input
                                type="checkbox"
                                checked={dd.permissions.can_delete}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    menuItem.id,
                                    dd.id,
                                    "can_delete",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoleDetails;
